// Intent (1.2.0): the LOGIN HUB — first screen of every login. Order mirrors
// cost and friction: Google (free, one tap) > e-mail magic link (free) > SMS
// (paid, kept as backup only). Mock: stitch prompt "login". Guest escape stays
// prominent — login is optional, never a wall.

import { router } from 'expo-router';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Pressable, ScrollView, TextInput, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { SafeAreaView } from 'react-native-safe-area-context';
import * as Google from 'expo-auth-session/providers/google';
import * as WebBrowser from 'expo-web-browser';

import { API_URL } from '@/api/client';
import { AppText } from '@/components/ui/app-text';
import { PrimaryButton } from '@/components/ui/primary-button';
import { ScreenHeader } from '@/components/ui/screen-header';
import { routeAfterLogin } from '@/store/auth-routing';
import { useAuthStore } from '@/store/auth-store';
import { colors, fonts, radius, spacing } from '@/theme/tokens';

// Required by expo-auth-session on web: closes the auth popup after redirect.
WebBrowser.maybeCompleteAuthSession();

const GOOGLE_WEB_CLIENT_ID = process.env.EXPO_PUBLIC_GOOGLE_WEB_CLIENT_ID ?? undefined;
const GOOGLE_ANDROID_CLIENT_ID = process.env.EXPO_PUBLIC_GOOGLE_ANDROID_CLIENT_ID ?? undefined;
const GOOGLE_IOS_CLIENT_ID = process.env.EXPO_PUBLIC_GOOGLE_IOS_CLIENT_ID ?? undefined;

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]{2,}$/;

export default function AuthLoginScreen() {
  const { t } = useTranslation();
  const startEmailLogin = useAuthStore((s) => s.startEmailLogin);
  const loginWithGoogle = useAuthStore((s) => s.loginWithGoogle);

  const [email, setEmail] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const [googleBusy, setGoogleBusy] = useState(false);

  // id_token flow: the token goes straight to our backend for verification —
  // the app never talks to Google APIs itself. Hooks can't be conditional, so
  // until the Client IDs from Google Console land in EXPO_PUBLIC_GOOGLE_*_CLIENT_ID
  // the hook gets a harmless placeholder (never prompted — onGoogle guards it).
  const googleConfigured = Boolean(GOOGLE_WEB_CLIENT_ID || GOOGLE_ANDROID_CLIENT_ID || GOOGLE_IOS_CLIENT_ID);
  const [request, response, promptAsync] = Google.useIdTokenAuthRequest({
    clientId: GOOGLE_WEB_CLIENT_ID ?? 'unconfigured.apps.googleusercontent.com',
    androidClientId: GOOGLE_ANDROID_CLIENT_ID,
    iosClientId: GOOGLE_IOS_CLIENT_ID,
  });

  useEffect(() => {
    if (response?.type !== 'success') return;
    const idToken = (response.params as { id_token?: string }).id_token;
    if (!idToken) return;
    void (async () => {
      setGoogleBusy(true);
      const result = await loginWithGoogle(idToken);
      setGoogleBusy(false);
      if (result.ok) {
        routeAfterLogin();
      } else {
        setError(result.message ?? t('auth.genericError'));
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [response]);

  const onGoogle = async () => {
    setError(null);
    if (!API_URL) {
      // Mock transport: skip the real Google window, log in as the demo account.
      setGoogleBusy(true);
      const result = await loginWithGoogle('mock-google-id-token');
      setGoogleBusy(false);
      if (result.ok) routeAfterLogin();
      else setError(result.message ?? t('auth.genericError'));
      return;
    }
    if (!googleConfigured || !request) {
      setError(t('auth.googleSoon'));
      return;
    }
    await promptAsync();
  };

  const onSendEmail = async () => {
    setSending(true);
    setError(null);
    const result = await startEmailLogin(email);
    setSending(false);
    if (result.ok) {
      router.push('/auth/email-sent');
    } else {
      setError(result.message ?? t('auth.genericError'));
    }
  };

  const emailValid = EMAIL_RE.test(email.trim());

  return (
    <SafeAreaView style={{ flex: 1, backgroundColor: colors.background }} edges={['top', 'bottom']}>
      <ScrollView
        contentContainerStyle={{ padding: spacing.screen, gap: spacing.md, paddingBottom: spacing.xl }}
        keyboardShouldPersistTaps="handled"
      >
        <ScreenHeader title={t('auth.loginTitle')} />

        <AppText variant="headlineLg" color={colors.navy}>
          {t('auth.hubTitle')}
        </AppText>
        <AppText variant="bodyLg">{t('auth.hubSubtitle')}</AppText>

        {/* Google — the "click, pick your account, done" path */}
        <Pressable
          accessibilityRole="button"
          onPress={onGoogle}
          disabled={googleBusy}
          style={{
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'center',
            gap: spacing.sm,
            backgroundColor: colors.navy,
            borderRadius: radius.card,
            minHeight: 56,
            opacity: googleBusy ? 0.6 : 1,
          }}
        >
          <Ionicons name="logo-google" size={20} color="#fff" />
          <AppText variant="labelLg" color="#fff">
            {googleBusy ? t('auth.googleBusy') : t('auth.googleButton')}
          </AppText>
        </Pressable>

        <View style={{ flexDirection: 'row', alignItems: 'center', gap: spacing.sm }}>
          <View style={{ flex: 1, height: 1, backgroundColor: colors.outlineVariant }} />
          <AppText variant="labelMd">{t('auth.orDivider')}</AppText>
          <View style={{ flex: 1, height: 1, backgroundColor: colors.outlineVariant }} />
        </View>

        {/* E-mail magic link — universal free fallback */}
        <View style={{ gap: spacing.base }}>
          <AppText variant="labelMd" color={colors.primary}>
            {t('auth.emailLabel')}
          </AppText>
          <View
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              backgroundColor: colors.primaryTile,
              borderRadius: radius.card,
              paddingHorizontal: spacing.md,
              minHeight: 56,
              gap: spacing.sm,
            }}
          >
            <TextInput
              value={email}
              onChangeText={(text) => setEmail(text)}
              placeholder="twoj@email.pl"
              placeholderTextColor={colors.outlineVariant}
              keyboardType="email-address"
              autoCapitalize="none"
              autoCorrect={false}
              style={{
                flex: 1,
                fontFamily: fonts.semiBold,
                fontSize: 18,
                color: colors.navy,
                paddingVertical: spacing.sm,
              }}
            />
            <Ionicons name="mail-outline" size={22} color={colors.primary} />
          </View>
          <AppText variant="labelMd">{t('auth.emailHint')}</AppText>
        </View>

        {error && (
          <AppText variant="bodyMd" color={colors.warning}>
            {error}
          </AppText>
        )}

        <PrimaryButton label={t('auth.sendLink')} disabled={!emailValid || sending} onPress={onSendEmail} />

        {/* SMS stays as the backup path (paid — not promoted) */}
        <Pressable
          accessibilityRole="button"
          onPress={() => router.push('/auth/phone')}
          style={{ minHeight: 44, justifyContent: 'center' }}
        >
          <AppText variant="labelLg" color={colors.primary} style={{ textAlign: 'center' }}>
            {t('auth.phoneFallback')}
          </AppText>
        </Pressable>

        <Pressable
          accessibilityRole="button"
          onPress={() => router.back()}
          style={{ minHeight: 44, justifyContent: 'center' }}
        >
          <AppText variant="labelLg" color={colors.primary} style={{ textAlign: 'center' }}>
            {t('auth.stayGuest')}
          </AppText>
        </Pressable>

        <View
          style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: spacing.xs, marginTop: spacing.md }}
        >
          <Ionicons name="shield-checkmark-outline" size={16} color={colors.outlineVariant} />
          <AppText variant="labelMd">{t('auth.hubFooter')}</AppText>
        </View>
      </ScrollView>
    </SafeAreaView>
  );
}
